import React from 'react'
import classNames from 'classnames'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import type { UseFormRegisterReturn, FieldErrors } from 'react-hook-form'

interface GroupedOption {
  label: string
  value: string
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name: string
  // A list of labelled options keeps its order; a Record does not when its
  // keys are ids, since JavaScript puts integer keys first, ascending.
  options?: (string | number)[] | GroupedOption[] | Record<string | number, string>
  disabledOptions?: string[]
  groupedOptions?: Record<string, GroupedOption[]>
  // Optional so the component can also be used controlled (value/onChange)
  // outside a react-hook-form context. RHF callers keep passing register.
  register?: Partial<UseFormRegisterReturn>
  errors?: FieldErrors
  placeholder?: string
  containerClass?: string
}

const Select = (props: SelectProps) => {
  const {
    name,
    options,
    disabledOptions,
    groupedOptions,
    register = {},
    errors,
    placeholder,
    className,
    containerClass = 'sm:max-w-xs',
    value,
    ...otherProps
  } = props
  // Controlled when a `value` is supplied (no register); uncontrolled
  // (defaultValue) for the react-hook-form path.
  const controlled = value !== undefined
  const classes = classNames(
    'block w-full shadow-xs pr-10 pl-7 sm:text-sm rounded-md',
    {
      'border-cerise-red-300 text-cerise-red-900 placeholder-red-300 focus:outline-hidden focus:ring-cerise-red focus:border-cerise-red': errors?.[name],
      'focus:ring-perfect-shine focus:border-perfect-shine block w-full border-gray-300': !errors?.[name],
    },
    className,
  )

  return (
    <div className="flex flex-col">
      <div className={`relative ${containerClass}`}>
        <select
          {...otherProps}
          {...register}
          className={classes}
          {...(controlled ? { value } : { defaultValue: '' })}
        >
          <option value="" disabled hidden>{placeholder}</option>
          {options && (Array.isArray(options)
            ? options.map((option) => {
              const { value: optionValue, label } = typeof option === 'object' ? option : { value: option, label: option }
              return (
                <option key={optionValue} value={optionValue} {...disabledOptions?.includes(String(optionValue)) ? { disabled: true } : {}}>{label}</option>
              )
            })
            : Object.keys(options).map((key) => (
              <option key={key} value={key} {...disabledOptions?.includes((options as Record<string, string>)[key]) ? { disabled: true } : {}}>{(options as Record<string, string>)[key]}</option>
            ))
          )
          }
          {groupedOptions && (Object.keys(groupedOptions).map((groupLabel, key) => (
            <optgroup label={groupLabel} key={key}>
              {
                groupedOptions[groupLabel].map(({ label, value }) => (
                  <option className="mb-4 pb-4" key={label} value={value}>{label}</option>
                ))
              }
            </optgroup>
          ))
          )
          }
        </select>
        {errors?.[name] && (
          <div className="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-cerise-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
      {errors && (
        <p className="mt-2 text-sm text-cerise-red-600 select-none min-h-5">
          {(errors?.[name]?.message as string) || ''}
        </p>
      )}
    </div>
  )
}

export default Select
